<template>
  <div class="step-container advisor" data-test-id="account-features-container">
    <v-form name="AccountFeatures" class="form" data-test-id="account-features-form">
      <v-container class="features-step-container">
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1>
              <span class="headerlight">
                Select your <strong>account features.</strong>
              </span>
            </h1>
          </v-col>
        </v-row>

        <!-- No Accounts -->
        <div class="alert" v-if="noAccountsChosen" data-test-id="no-accounts-alert">
          <div class="pr-2">
            <span class="fa-icon left">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
          </div>
          <div>
            You must complete the Account Selection screen before choosing
            features.
          </div>
        </div>

        <!-- Client One -->
        <div
          class="justify-center features-table"
          v-if="accountTypesOne.length > 0"
          data-test-id="client-one-features"
        >
          <v-row class="header-row">
            <v-col class="col-6 first">
              <h4>{{ clientOneName }}</h4>
            </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.MONEY_LINK }} </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.ACAT }} </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.IRA_DISTRIBUTION }} </v-col>
          </v-row>
          <v-row
            v-for="(account, index) in accountTypesOne"
            :key="account.nickname"
            :data-test-id="'client-one-account-' + index"
          >
            <v-col class="col-6 first">
              {{ account.name }} ({{ account.nickname }})

              <v-select
                v-model="account.masterAccountNumber"
                :items="masterAccountNumbers"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Master Account Number"
                hide-details="auto"
                dense
                @change="$v.accountTypesOne.$each[index].masterAccountNumber.$touch()"
                :error-messages="accountTypesOneErrors(index)"
                :data-test-id="'client-one-master-account-' + index"
              >
              </v-select>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                dense
                :ripple="false"
                v-model="account.moveMoney"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="!isOfflineAccount(account)"
                :data-test-id="'client-one-money-link-' + index"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.MONEY_LINK }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                dense
                :ripple="false"
                v-model="account.acat"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="!isOfflineAccount(account)"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.ACAT }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                dense
                :ripple="false"
                v-model="account.iraDistribution"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="account.name === ACCOUNT_TYPES.IRA || account.name === ACCOUNT_TYPES.SEP_IRA || account.name === ACCOUNT_TYPES.MANAGED_IRA"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.IRA_DISTRIBUTION }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </div>

        <!-- Client 2 -->
        <div
          class="justify-center features-table mt-7"
          v-if="accountTypesTwo.length > 0"
          data-test-id="client-two-features"
        >
          <v-row class="header-row">
            <v-col class="col-6 first">
              <h4>{{ clientTwoName }}</h4>
            </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.MONEY_LINK }} </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.ACAT }} </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.IRA_DISTRIBUTION }} </v-col>
          </v-row>
          <v-row
            v-for="(account, index) in accountTypesTwo"
            :key="account.nickname"
            :data-test-id="'client-two-account-' + index"
          >
            <v-col class="col-6 first">
              {{ account.name }} ({{ account.nickname }})
              <v-select
                v-model="account.masterAccountNumber"
                :items="masterAccountNumbers"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Master Account Number"
                hide-details="auto"
                dense
                @change="$v.accountTypesTwo.$each[index].masterAccountNumber.$touch()"
                :error-messages="accountTypesTwoErrors(index)"
                :data-test-id="'client-two-master-account-' + index"
              >
              </v-select>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="account.moveMoney"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="!isOfflineAccount(account)"
                :data-test-id="'client-two-money-link-' + index"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.MONEY_LINK }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="account.acat"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="!isOfflineAccount(account)"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.ACAT }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                dense
                :ripple="false"
                v-model="account.iraDistribution"
                :color="primaryColor"
                @change="$v.$touch()"
                v-if="account.name === ACCOUNT_TYPES.IRA || account.name === ACCOUNT_TYPES.SEP_IRA || account.name === ACCOUNT_TYPES.MANAGED_IRA"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.IRA_DISTRIBUTION }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </div>

        <!-- Joint -->
        <div
          class="justify-center features-table mt-7"
          v-if="accountTypesJoint.length > 0"
          data-test-id="joint-features"
        >
          <v-row class="header-row">
            <v-col class="col-8 first">
              <h4>{{ jointName }}</h4>
            </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.MONEY_LINK }} </v-col>
            <v-col class="col-2"> {{ FEATURES_TYPES.ACAT }} </v-col>
          </v-row>
          <v-row
            v-for="(account, index) in accountTypesJoint"
            :key="account.nickname"
            :data-test-id="'joint-account-' + index"
          >
            <v-col class="col-8 first">
              {{ account.name }} ({{ account.nickname }})
              <v-select
                v-model="account.masterAccountNumber"
                :items="masterAccountNumbers"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Master Account Number"
                hide-details="auto"
                dense
                @change="$v.accountTypesJoint.$each[index].masterAccountNumber.$touch()"
                :error-messages="accountTypesJointErrors(index)"
                :data-test-id="'joint-master-account-' + index"
              >
              </v-select>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="account.moveMoney"
                :color="primaryColor"
                @change="$v.$touch()"
                :data-test-id="'joint-money-link-' + index"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.MONEY_LINK }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="col-2">
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="account.acat"
                :color="primaryColor"
                @change="$v.$touch()"
              >
                <template v-slot:label>
                  <span class="cb-label mobile-label">
                    {{ FEATURES_TYPES.ACAT }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-form>
    <bottom-nav 
      v-on:go-prev="prev()" 
      v-on:go-next="next()"
      :has-skip="false"
    ></bottom-nav>
  </div>
</template>
<script>
import BottomNav from '@/components/common/BottomNav';
import AdvisorClientMixin from '@/common/mixins/advisor.client.mixin';
import { MASTER_ACCOUNT_NUMBER } from '@/stores/actions.type';
import { MENUS, FEATURES_TYPES } from '@/common/constants';
import {
  NOTIFICATION_TYPES,
  NotificationService,
} from '@/services/notification.service';
import { mapGetters } from 'vuex';

export default {
  name: 'AccountFeatures',

  mixins: [AdvisorClientMixin],
  components: { BottomNav },

  data: () => ({
    accountTypesOne: [],
    accountTypesTwo: [],
    accountTypesJoint: [],
    masterAccountNumbers: [],
  }),

  validations() {
    return {
      accountTypesOne: {
        $each: {
          masterAccountNumber: {
            required: () => true,
          },
        },
      },
      accountTypesTwo: {
        $each: {
          masterAccountNumber: {
            required: () => true,
          },
        },
      },
      accountTypesJoint: {
        $each: {
          masterAccountNumber: {
            required: () => true,
          },
        },
      },
      validationGroup: [
        'accountTypesOne',
        'accountTypesTwo',
        'accountTypesJoint',
      ],
    };
  },

  computed: {
    ...mapGetters(['currentCompany']),

    noAccountsChosen() {
      // It is assumed that the account selection page
      // cannot be left without client 1 and client 2
      // having chosen at least one account

      return (
        this.accountTypesOne.length === 0 &&
        this.accountTypesTwo.length === 0 &&
        this.accountTypesJoint.length === 0
      );
    },
    jointName() {
      return `${this.clientOneName} and ${this.clientTwoName}`;
    },
  },

  methods: {
    isOfflineAccount(account) {
      return account.name.includes('offline');
    },
    /* Validations for account types - always return empty array since master account numbers are optional */
    accountTypesOneErrors() {
      return [];
    },
    accountTypesTwoErrors() {
      return [];
    },
    accountTypesJointErrors() {
      return [];
    },

    /* Get Master Account Numbers */
    getMasterAccountNumbers() {
      this.$store
        .dispatch(MASTER_ACCOUNT_NUMBER, {
          organisationId: this.currentCompany.id,
        })
        .then((response) => {
          // Add a 'None' option to the master account numbers
          this.masterAccountNumbers = [
            { text: 'None', value: null },
            ...response
          ];
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: 'Error getting Master Account Numbers!',
              okTitle: 'Ok',
            },
            response,
          );
        });
    },

    /* Initialize Account Features object for view */
    initializeAccountFeatures() {
      // Get Master Account Numbers
      this.getMasterAccountNumbers();

      const accountFeatures = this.getAccountFeatureSelections();
      const findByName = (name) => accountFeatures.find((f) => f.name === name);

      // Client One Features
      let clientFeatures = findByName(this.clientOneName);
      if (clientFeatures) {
        this.accountTypesOne = clientFeatures.features;
      }

      // Client Two Features
      clientFeatures = findByName(this.clientTwoName);
      if (clientFeatures) {
        this.accountTypesTwo = clientFeatures.features;
      }

      // Joint Features
      clientFeatures = findByName(this.jointName);
      if (clientFeatures) {
        this.accountTypesJoint = clientFeatures.features;
      }
    },

    /* Set the features by client/account in the working client */
    setAccountFeatures() {
      // Set Client One Account Features
      this.accountTypesOne.forEach((a) => {
        let accountObj = this.clientOne.accounts.find(
          (ca) => ca.label === a.nickname,
        );
        if (accountObj) {
          accountObj.moveMoney = a.moveMoney;
          accountObj.acat = a.acat;
          accountObj.iraDistribution = a.iraDistribution;
          accountObj.masterAccountNumber = a.masterAccountNumber;
        }
      });

      // Set Client Two Account Features
      this.accountTypesTwo.forEach((a) => {
        let accountObj = this.clientTwo.accounts.find(
          (ca) => ca.label === a.nickname,
        );
        if (accountObj) {
          accountObj.moveMoney = a.moveMoney;
          accountObj.acat = a.acat;
          accountObj.iraDistribution = a.iraDistribution;
          accountObj.masterAccountNumber = a.masterAccountNumber;
        }
      });

      // Set Joint Account Features on Client One and Client Two
      this.accountTypesJoint.forEach((a) => {
        let accountObj = this.clientOne.accounts.find(
          (ca) => ca.label === a.nickname,
        );
        if (accountObj) {
          accountObj.moveMoney = a.moveMoney;
          accountObj.acat = a.acat;
          accountObj.iraDistribution = a.iraDistribution;
          accountObj.masterAccountNumber = a.masterAccountNumber;
        }

        accountObj = this.clientTwo.accounts.find(
          (ca) => ca.label === a.nickname,
        );
        if (accountObj) {
          accountObj.moveMoney = a.moveMoney;
          accountObj.acat = a.acat;
          accountObj.iraDistribution = a.iraDistribution;
          accountObj.masterAccountNumber = a.masterAccountNumber;
        }
      });
    },

    /* Map form data to working client */
    mapData() {
      this.setAccountFeatures();
    },

    /* Nav to Next Page in flow */
    next(route = null) {
      this.$v.$touch();
      
      // Revalidate
      if (this.$v.$invalid) return;

      this.mapData();

      const to = route ? route.name : MENUS.ADVISOR.CLIENT.ADVISORY_RATE.id;
      this.goNext(to);
    },

    /* Legacy method - maintained for backwards compatibility but no longer directly called from UI */
    skip() {
      // Navigate to the next page
      const to = MENUS.ADVISOR.CLIENT.ADVISORY_RATE.id;
      this.goNext(to);
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(MENUS.ADVISOR.CLIENT.ACCOUNT_SELECTION.id);
    },
  },

  created() {
    this.FEATURES_TYPES = FEATURES_TYPES;

    // Initialize the current account features
    this.initializeAccountFeatures();

    // If No accounts have been chosen yet, don't mark the page as
    // visted yet. Accounts must exist for the client(s)
    // UNUSED
    this.currentClient.featuresVisited = !this.noAccountsChosen;
  },
};
</script>
<style lang="scss" scoped>
.features-step-container {
  max-width: 1024px;
  align-items: center;
  display: flex;
  flex-direction: column;
}
.features-table {
  width: 100%;
  max-width: 100%;
  border: 2px solid var(--secondary-color);
  border-radius: 10px;
  display: flex;
  flex-direction: column;

  .header-row {
    font-weight: 500;

    .col-2 {
      @media screen and (max-width: 960px) {
        display: none !important;
      }
    }
  }

  div:not(:first-of-type) {
    border-top: 2px solid var(--secondary-color);
  }

  .row {
    margin: 0;

    .cb-label {
      color: var(--color-text);
      font-size: 16px;
      font-weight: 500;
      margin-bottom: -5px;
    }

    .col {
      padding: 20px 26px;
      border-top: none;

      &.first {
        background-color: var(--color-light-grey);
      }

      @media screen and (max-width: 960px) {
        min-width: 100%;
      }

      .mobile-label {
        display: none;
        @media screen and (max-width: 960px) {
          display: inline-block;
        }
      }
    }

    .col-2 {
      justify-content: center;
      display: flex;
      align-items: center;

      @media screen and (max-width: 960px) {
        justify-content: start;
      }
    }

    div:not(:last-of-type) {
      border-right: 2px solid var(--secondary-color);

      @media screen and (max-width: 960px) {
        border-right: none;
      }
    }
  }
}
</style>
